/**
 * Settings → Roles / Team. User table + invite form + pending invites
 * list + edit user modal with double-confirm delete.
 *
 * Access model (migration 0098): роль аккаунта — Account Admin (полный
 * контроль + неявный админ на каждом приложении), Member (всё решает
 * пер-приложенческая таблица) или Finance Manager (счета и реквизиты
 * организации; реклама — только там, где выдана). Плюс пер-приложенческий
 * доступ: на каждое приложение один из Hidden / View Only / Ads Manager /
 * Admin. Оставленные Hidden приложения участнику не видны.
 *
 * Финменеджер в организации ОДИН: слот занят — опция гаснет, а последнее
 * слово всё равно за сервером (409 `finance_manager_already_exists`).
 */

import { Fragment, useEffect, useMemo, useState } from "react";
import { useDiscardGuard } from "@/lib/discard-guard";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import {
  useApps,
  useCreateUser,
  useDeleteUser,
  useResendInvitation,
  useUpdateUser,
  useUsers,
} from "@/lib/api-hooks";
import { describeApiError } from "@/lib/api-error";
import {
  ACCOUNT_LEVEL_ROLES,
  ASSIGNABLE_ROLES,
  assignableRoleOf,
  capAccessForRole,
  roleBadge,
} from "@/lib/roles";
import { useAuth } from "@/lib/useAuth";
import type {
  AppAccessItem,
  AppAccessLevel,
  AssignableRole,
  UserRow,
} from "@/lib/types";
import { StConfirmModal, StSectionHead } from "./_shared";

const LEVEL_OPTIONS: { value: AppAccessLevel; label: string }[] = [
  { value: "hidden", label: "Hidden" },
  { value: "view", label: "View Only" },
  { value: "ads_manager", label: "Ads Manager" },
  { value: "admin", label: "Admin" },
];

type AccessMap = Record<number, AppAccessLevel>;

function accessMapOf(u: UserRow): AccessMap {
  const m: AccessMap = {};
  for (const it of u.app_access) m[it.app_id] = it.level;
  return m;
}

function mapToItems(m: AccessMap): AppAccessItem[] {
  return Object.entries(m).map(([app_id, level]) => ({
    app_id: Number(app_id),
    level,
  }));
}

function AppAccessEditor({
  value,
  onChange,
  viewOnly = false,
}: {
  value: AccessMap;
  onChange: (next: AccessMap) => void;
  /** Роль Viewer: только Hidden и View Only. */
  viewOnly?: boolean;
}) {
  const apps = useApps();
  const rows = apps.data ?? [];
  const options = viewOnly
    ? LEVEL_OPTIONS.filter((o) => o.value === "hidden" || o.value === "view")
    : LEVEL_OPTIONS;
  return (
    <div className="field">
      <label className="label">Per-app access</label>
      {rows.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>
          {apps.isLoading ? "Loading apps…" : "No apps on this account yet."}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            maxHeight: 260,
            overflow: "auto",
            border: "1px solid var(--c-divider)",
            borderRadius: 10,
            padding: 8,
          }}
        >
          {rows.map((a) => (
            <div
              key={a.id}
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 13,
                }}
              >
                {a.name ?? a.bundle_id}
              </span>
              <select
                className="select"
                style={{ width: 150, flex: "0 0 auto" }}
                value={value[a.id] ?? "hidden"}
                onChange={(e) =>
                  onChange({
                    ...value,
                    [a.id]: e.target.value as AppAccessLevel,
                  })
                }
              >
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Селектор роли аккаунта. Один на форму приглашения и на модал правки —
 *  разойдясь, две копии однажды предложили бы разные наборы ролей. */
function RoleSelect({
  value,
  onChange,
  disabled,
  disabledNote,
  financeHolder,
}: {
  value: AssignableRole;
  onChange: (role: AssignableRole) => void;
  disabled?: boolean;
  /** Чем объяснить заблокированный селектор. */
  disabledNote?: string;
  /** Кто держит единственный слот финменеджера, если держит кто-то
   *  ДРУГОЙ. `null` — слот свободен. */
  financeHolder: string | null;
}) {
  const hint = ASSIGNABLE_ROLES.find((r) => r.value === value)?.hint;
  return (
    <div className="field">
      <label className="label">Role</label>
      <select
        className="select"
        style={{ maxWidth: 280 }}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as AssignableRole)}
      >
        {ASSIGNABLE_ROLES.map((r) => (
          <option
            key={r.value}
            value={r.value}
            disabled={r.value === "finance_manager" && financeHolder !== null}
          >
            {roleBadge(r.value).label}
          </option>
        ))}
      </select>
      <div
        className="muted"
        style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}
      >
        {disabled && disabledNote ? disabledNote : hint}
        {financeHolder !== null && (
          <div>
            One Finance Manager per organization — the slot is held by{" "}
            {financeHolder}.
          </div>
        )}
      </div>
    </div>
  );
}

function AccessCell({ u }: { u: UserRow }) {
  // Роль показываем, только когда она значит что-то помимо уровней по
  // приложениям: значок «Ads Manager» рядом с «Views 3» читался бы как
  // второй, противоречащий уровень доступа.
  const role = ACCOUNT_LEVEL_ROLES.includes(u.role) ? roleBadge(u.role) : null;
  const manage = u.app_access.filter(
    (a) => a.level === "ads_manager" || a.level === "admin"
  ).length;
  const view = u.app_access.filter((a) => a.level === "view").length;
  const apps =
    u.role === "admin"
      ? null
      : [manage > 0 && `Manages ${manage}`, view > 0 && `Views ${view}`]
          .filter(Boolean)
          .join(" · ");
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
    >
      {role && (
        <span className={u.role === "admin" ? "badge accent" : "badge"}>
          {role.label}
        </span>
      )}
      {apps ? (
        <span className="muted" style={{ fontSize: 12 }}>
          {apps}
        </span>
      ) : (
        !role && <span className="badge">No app access</span>
      )}
    </div>
  );
}

const LEVEL_LABEL: Record<AppAccessLevel, string> = {
  hidden: "Hidden",
  view: "View Only",
  ads_manager: "Ads Manager",
  admin: "Admin",
};

function LevelBadge({ level }: { level: AppAccessLevel }) {
  const cls =
    level === "admin"
      ? "badge accent"
      : level === "ads_manager"
        ? "badge enabled"
        : "badge";
  return (
    <span className={cls} style={level === "hidden" ? { opacity: 0.5 } : undefined}>
      {LEVEL_LABEL[level]}
    </span>
  );
}

/** Read-only expandable panel: every app on the account + this user's level
 *  next to it — a quick "where does this user have access" view without
 *  opening the Edit modal. */
function AccessBreakdown({ u }: { u: UserRow }) {
  const apps = useApps();
  const rows = apps.data ?? [];
  const map = accessMapOf(u);
  return (
    <div
      style={{
        padding: "10px 14px 12px 50px",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: "4px 24px",
      }}
    >
      {u.role === "admin" && (
        <div className="muted" style={{ fontSize: 12, gridColumn: "1 / -1" }}>
          Account Admin — full access to every app.
        </div>
      )}
      {rows.length === 0 && (
        <span className="muted" style={{ fontSize: 12 }}>
          No apps on this account yet.
        </span>
      )}
      {rows.map((a) => {
        const level: AppAccessLevel =
          u.role === "admin" ? "admin" : map[a.id] ?? "hidden";
        return (
          <div
            key={a.id}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              fontSize: 12.5,
              padding: "2px 0",
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {a.name ?? a.bundle_id}
            </span>
            <LevelBadge level={level} />
          </div>
        );
      })}
    </div>
  );
}

export function RolesTeamTab() {
  const usersQuery = useUsers();
  const { user: me } = useAuth();
  const create = useCreateUser();
  const update = useUpdateUser();
  const del = useDeleteUser();
  const resend = useResendInvitation();

  const [editing, setEditing] = useState<UserRow | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggleExpand = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [invite, setInvite] = useState({
    email: "",
    first: "",
    last: "",
    role: "ads_manager" as AssignableRole,
    access: {} as AccessMap,
  });

  const all = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const active = useMemo(() => all.filter((u) => u.status !== "pending"), [all]);
  const pending = useMemo(() => all.filter((u) => u.status === "pending"), [all]);

  // Кто уже занимает единственный слот финменеджера. Приглашения в
  // ожидании считаем занятыми: строка участия у них есть, и сервер
  // откажет так же, как по действующему.
  const financeManager = useMemo(
    () => all.find((u) => u.role === "finance_manager") ?? null,
    [all]
  );
  const holderName = (u: UserRow) => u.full_name ?? u.email;

  const submitInvite = async () => {
    if (!invite.email.trim()) return;
    setInviteError(null);
    const fullName =
      [invite.first, invite.last].filter(Boolean).join(" ").trim() ||
      invite.email;
    try {
      await create.mutateAsync({
        email: invite.email.trim(),
        full_name: fullName,
        role: invite.role,
        app_access: invite.role === "admin" ? [] : mapToItems(invite.access),
      });
    } catch (e) {
      // ★ Отказы приходят готовой английской фразой и называют, что
      // делать: квота тарифа, занятый слот финменеджера (409
      // `finance_manager_already_exists`). Без показа они были бы
      // молчаливыми: форма просто не срабатывала бы, и человек решил
      // бы, что сломано.
      setInviteError(describeApiError(e, "Could not send the invitation."));
      return;
    }
    setInvite({
      email: "",
      first: "",
      last: "",
      role: "ads_manager",
      access: {},
    });
  };

  return (
    <>
      <StSectionHead
        title="Roles / Team"
        desc="Account Admins manage the whole account. The Finance Manager handles billing and organization details — one per organization. Everyone else gets per-app access: for each app pick Hidden, View Only, Ads Manager, or Admin. A Viewer only sees the apps granted to them and can't change anything."
      />

      <div className="card" style={{ marginBottom: 18 }}>
        <table className="dt" style={{ minWidth: 0 }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Access</th>
              <th style={{ width: 120, textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {usersQuery.isLoading && (
              <tr>
                <td colSpan={4} className="muted" style={{ padding: 20 }}>
                  Loading…
                </td>
              </tr>
            )}
            {active.map((u) => (
              <Fragment key={u.id}>
                <tr
                  onClick={() => toggleExpand(u.id)}
                  style={{ cursor: "pointer" }}
                  title="Click to see per-app access"
                >
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <span
                        className="muted"
                        style={{
                          fontSize: 10,
                          width: 12,
                          display: "inline-block",
                          transform: expanded.has(u.id)
                            ? "rotate(90deg)"
                            : "none",
                          transition: "transform 120ms",
                        }}
                      >
                        ▶
                      </span>
                      <div
                        className="avatar"
                        style={{ width: 26, height: 26, fontSize: 11 }}
                      >
                        {(u.full_name ?? u.email)
                          .split(" ")
                          .map((s) => s[0])
                          .slice(0, 2)
                          .join("")}
                      </div>
                      <b>{u.full_name ?? "—"}</b>
                    </div>
                  </td>
                  <td className="muted">{u.email}</td>
                  <td>
                    <AccessCell u={u} />
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Btn
                      icon={I.edit}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing(u);
                      }}
                    >
                      Edit
                    </Btn>
                  </td>
                </tr>
                {expanded.has(u.id) && (
                  <tr>
                    <td
                      colSpan={4}
                      style={{ padding: 0, background: "var(--c-surface-alt)" }}
                    >
                      <AccessBreakdown u={u} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 12px" }}>
        Invite New Users
      </h3>
      <div className="card card-pad" style={{ marginBottom: 14 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(240px, 2fr) minmax(140px, 1fr) minmax(140px, 1fr)",
            gap: 12,
          }}
        >
          <div className="field">
            <label className="label">Email</label>
            <input
              className="input"
              placeholder="name@company.com"
              value={invite.email}
              onChange={(e) =>
                setInvite((v) => ({ ...v, email: e.target.value }))
              }
            />
          </div>
          <div className="field">
            <label className="label">First name</label>
            <input
              className="input"
              value={invite.first}
              onChange={(e) =>
                setInvite((v) => ({ ...v, first: e.target.value }))
              }
            />
          </div>
          <div className="field">
            <label className="label">Last name</label>
            <input
              className="input"
              value={invite.last}
              onChange={(e) =>
                setInvite((v) => ({ ...v, last: e.target.value }))
              }
            />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <RoleSelect
            value={invite.role}
            onChange={(role) =>
              setInvite((v) => ({ ...v, role, access: capAccessForRole(role, v.access) }))
            }
            financeHolder={financeManager ? holderName(financeManager) : null}
          />
        </div>

        {invite.role !== "admin" && (
          <div style={{ marginTop: 12 }}>
            <AppAccessEditor
              value={invite.access}
              viewOnly={invite.role === "viewer"}
              onChange={(access) => setInvite((v) => ({ ...v, access }))}
            />
          </div>
        )}

        {inviteError && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              borderRadius: "var(--r-md)",
              background: "var(--c-neg-50)",
              color: "var(--c-neg-600)",
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
          >
            {inviteError}
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <Btn
            variant="primary"
            disabled={create.isPending || !invite.email.trim()}
            onClick={() => void submitInvite()}
          >
            {create.isPending ? "Inviting…" : "Invite"}
          </Btn>
        </div>
      </div>

      {pending.length > 0 && (
        <div className="card">
          <div
            className="card-head"
            style={{ padding: "12px 16px", borderBottom: "1px solid var(--c-divider)" }}
          >
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
              Pending invites
            </h3>
          </div>
          <table className="dt" style={{ minWidth: 0 }}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Access</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <tr key={p.id}>
                  <td className="muted">{p.email}</td>
                  <td>{p.full_name ?? "—"}</td>
                  <td>
                    <AccessCell u={p} />
                  </td>
                  <td className="muted">
                    <span className="badge warn">
                      <span className="dot" />
                      Pending
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <Btn icon={I.edit} onClick={() => setEditing(p)}>
                        Edit
                      </Btn>
                      <Btn
                        disabled={resend.isPending}
                        onClick={() => void resend.mutate(p.id)}
                      >
                        Resend
                      </Btn>
                      <Btn
                        variant="danger"
                        disabled={me?.id === p.id}
                        onClick={() => void del.mutate(p.id)}
                      >
                        Delete
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditUserModal
          user={editing}
          isSelf={me?.id === editing.id}
          financeHolder={
            financeManager && financeManager.id !== editing.id
              ? holderName(financeManager)
              : null
          }
          onClose={() => setEditing(null)}
          onApply={async (patch) => {
            await update.mutateAsync({ id: editing.id, ...patch });
            setEditing(null);
          }}
          onDelete={async () => {
            await del.mutateAsync(editing.id);
            setEditing(null);
          }}
          canDelete={me?.id !== editing.id}
        />
      )}
    </>
  );
}

function EditUserModal({
  user,
  isSelf,
  financeHolder,
  onClose: onCloseProp,
  onApply,
  onDelete,
  canDelete,
}: {
  user: UserRow;
  isSelf: boolean;
  /** Кто держит слот финменеджера, если это НЕ правимый участник. */
  financeHolder: string | null;
  onClose: () => void;
  onApply: (patch: {
    full_name?: string | null;
    role?: AssignableRole;
    app_access?: AppAccessItem[];
    is_active?: boolean;
  }) => Promise<void>;
  onDelete: () => Promise<void>;
  canDelete: boolean;
}) {
  const [v, setV] = useState({
    name: user.full_name ?? "",
    role: assignableRoleOf(user.role),
    access: accessMapOf(user),
    is_active: user.is_active,
  });
  const [confirmDel, setConfirmDel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setV({
      name: user.full_name ?? "",
      role: assignableRoleOf(user.role),
      access: accessMapOf(user),
      is_active: user.is_active,
    });
    setError(null);
  }, [user]);

  const guard = useDiscardGuard(true, JSON.stringify(v) !== JSON.stringify({ name: user.full_name ?? "", role: assignableRoleOf(user.role), access: accessMapOf(user), is_active: user.is_active }), onCloseProp);
  const onClose = guard.requestClose;

  // Отказ обязан долететь до экрана: `onApply` бросает (занятый слот
  // финменеджера, квота тарифа), и без перехвата модал просто застывал
  // бы открытым, ничего не сказав.
  const apply = async () => {
    setError(null);
    setSaving(true);
    try {
      await onApply({
        full_name: v.name || null,
        // ★ Роль шлём, только когда её ПРАВИЛИ: при правке имени запись
        // роли не трогается.
        role: v.role === assignableRoleOf(user.role) ? undefined : v.role,
        app_access: v.role === "admin" ? [] : mapToItems(v.access),
        is_active: v.is_active,
      });
    } catch (e) {
      setError(describeApiError(e, "Could not save the changes."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,18,30,.45)",
          zIndex: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 520,
            maxHeight: "90vh",
            overflow: "auto",
            background: "var(--c-surface)",
            borderRadius: 14,
            padding: 22,
            boxShadow: "var(--sh-3)",
          }}
        >
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Edit user</h3>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginTop: 16,
            }}
          >
            <div className="field">
              <label className="label">Name</label>
              <input
                className="input"
                value={v.name}
                onChange={(e) => setV({ ...v, name: e.target.value })}
              />
            </div>
            <div className="field">
              <label className="label">
                Email{" "}
                <span className="muted" style={{ fontWeight: 400 }}>
                  · login
                </span>
              </label>
              <input
                className="input"
                value={user.email}
                readOnly
                style={{ background: "var(--c-surface-sunk)" }}
              />
            </div>

            <RoleSelect
              value={v.role}
              onChange={(role) =>
                setV({ ...v, role, access: capAccessForRole(role, v.access) })
              }
              disabled={isSelf}
              disabledNote="You can't change your own role."
              financeHolder={financeHolder}
            />

            {v.role !== "admin" && (
              <AppAccessEditor
                value={v.access}
                viewOnly={v.role === "viewer"}
                onChange={(access) => setV({ ...v, access })}
              />
            )}

            <label className="check" style={{ marginTop: 4 }}>
              <input
                type="checkbox"
                checked={v.is_active}
                onChange={(e) => setV({ ...v, is_active: e.target.checked })}
              />
              <span>Account active</span>
            </label>

            {error && (
              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: "var(--r-md)",
                  background: "var(--c-neg-50)",
                  color: "var(--c-neg-600)",
                  fontSize: 12.5,
                  lineHeight: 1.5,
                }}
              >
                {error}
              </div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <Btn
              variant="danger"
              disabled={!canDelete}
              onClick={() => setConfirmDel(true)}
            >
              Delete user
            </Btn>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn onClick={onClose}>Cancel</Btn>
              <Btn variant="primary" disabled={saving} onClick={() => void apply()}>
                {saving ? "Saving…" : "Apply"}
              </Btn>
            </div>
          </div>
        </div>
      </div>
      <StConfirmModal
        open={confirmDel}
        title="Delete user?"
        body={`${user.email} will lose access immediately. This cannot be undone.`}
        danger
        doubleConfirm
        onCancel={() => setConfirmDel(false)}
        onConfirm={() => {
          setConfirmDel(false);
          void onDelete();
        }}
      />
    </>
  );
}
